<template>
  <div class="flex h-full min-h-0 bg-white border-l border-black/5">
    <div class="flex flex-1 min-h-0 w-full flex-col">
      <div class="relative flex-1 min-h-0">
        <div
          class="h-full w-full overflow-auto flex justify-center items-center"
        >
          <!-- Main image area -->
          <div class="max-w-full max-h-full p-4 w-full flex justify-center">
            <!-- <div v-if="errorMsg" class="p-4 text-sm text-red-600 bg-red-50 rounded max-w-xl">
              {{ errorMsg }}
              <div v-if="isLikelyUnsupported" class="mt-2 text-xs text-gray-600">
                This format may not be supported by your browser
                (<span class="font-mono">{{ inferredMime || props.type || ext }}</span>).
                You can still <button @click="downloadImage" class="underline">download</button>.
              </div>
            </div>

            <div v-else-if="!imgSource" class="p-6 text-sm text-gray-600">
              Loading image…
            </div> -->

            <img
              ref="imgEl"
              :src="imgSource"
              :alt="title"
              class="max-w-full max-h-[85vh] object-contain select-none"
              @error="onImgTagError"
              draggable="false"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import apiClient from "@/services/api";
import { useApplicationStore } from "@/stores/ApplicationStore";
import DropDown from "@/components/DropDown.vue";
import { onDownload } from "@/helpers/download";
import { storeToRefs } from "pinia";
import { usePathsStore } from "@/stores/CartStore";
import { makeBaseItems } from "@/utils/makeBaseItems";

const props = defineProps({
  name: { type: String, default: "" },
  file: { type: String, default: "" },
  type: { type: String, default: "" }, // optional MIME
});

// ── State ─────────────────────────────────────────────────────────────────────
const imgEl = ref(null);
const imgSource = ref(null); // blob: URL
let currentObjectUrl = null;
const errorMsg = ref("");
const inferredMime = ref("");
let lastHeaders = null;

const app = useApplicationStore();
const { current_selected } = storeToRefs(app);
const pathsStore = usePathsStore();

const baseItems = makeBaseItems({
  onDownload,
  apiClient: () => apiClient,
  selection: () => current_selected.value, // if it's a ref
});

const items = computed(() => {
  const extra = pathsStore.hasName(props.name)
    ? []
    : [
        {
          type: "item",
          label: "add to the Download cart",
          icon: "mdi:file-document-add",
          action: () => addToMultipleDownload(),
        },
      ];
  return [...baseItems.value, ...extra];
});

function addToMultipleDownload() {
  const obj = { path: current_selected.value.path, name: props.name };
  pathsStore.add(obj);
}

// ── Derived ──────────────────────────────────────────────────────────────────
const fileName = computed(
  () => (props.name || props.file?.split("/")?.pop() || "").split("?")[0],
);

const title = computed(() => fileName.value);

const ext = computed(() => {
  // Use name first — it's the actual filename; fall back to URL path stripped of query
  const raw = props.name || props.file || "";
  const withoutQuery = raw.split("?")[0].split("#")[0];
  const n = withoutQuery.toLowerCase();
  const i = n.lastIndexOf(".");
  return i >= 0 ? n.slice(i + 1) : "";
});

const knownImageExts = new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "bmp",
  "svg",
  "ico",
  "cur",
  // tricky / partially supported formats
  "tif",
  "tiff",
  "heic",
  "heif",
  "avif",
]);

const isImage = computed(() => {
  if (props.type?.startsWith("image/")) return true;
  return knownImageExts.has(ext.value);
});

const isLikelyUnsupported = computed(() => {
  const e = ext.value;
  // Most browsers: PNG/JPEG/GIF/WebP/SVG/BMP/ICO OK.
  // Often unsupported: TIFF/HEIC/HEIF (and sometimes AVIF).
  return ["tif", "tiff", "heic", "heif"].includes(e);
});

function mimeFromExt(e) {
  switch (e) {
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "bmp":
      return "image/bmp";
    case "svg":
      return "image/svg+xml";
    case "ico":
      return "image/x-icon";
    case "cur":
      return "image/x-icon";
    case "avif":
      return "image/avif"; // supported in many modern browsers
    case "tif":
    case "tiff":
      return "image/tiff"; // usually NOT supported natively
    case "heic":
      return "image/heic"; // usually NOT supported natively
    case "heif":
      return "image/heif"; // usually NOT supported natively
    default:
      return "";
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function filenameFromHeaders(headers, fallback) {
  try {
    const cd =
      headers?.["content-disposition"] || headers?.get?.("content-disposition");
    if (!cd) return fallback;
    const m = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(cd);
    if (m && m[1]) return decodeURIComponent(m[1].replace(/\"/g, ""));
  } catch {}
  return fallback;
}

function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "image";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function onImgTagError() {
  // Keep it generic—<img> doesn't expose decoding details
  errorMsg.value = "The browser failed to load or decode this image.";
}

// ── Loader ───────────────────────────────────────────────────────────────────
async function loadImage() {
  cleanupObjectUrl();
  errorMsg.value = "";
  imgSource.value = null;
  inferredMime.value = "";

  if (!isImage.value || !props.file) return;

  // If your backend supports direct public URLs with range/static serving,
  // you can skip fetching and set imgSource.value = props.file instead.
  const res = await apiClient.getImage?.(props.file, {
    responseType: "arraybuffer",
  });

  lastHeaders = res.headers || null;

  const contentType = (res.headers?.["content-type"] || "").toLowerCase();
  if (contentType) inferredMime.value = contentType;

  const bytes = res.data;
  if (!bytes || (bytes.byteLength !== undefined && bytes.byteLength === 0)) {
    throw new Error("Empty response body");
  }

  const mime =
    contentType ||
    props.type ||
    mimeFromExt(ext.value) ||
    "application/octet-stream";
  const blob = new Blob([bytes], { type: mime });

  currentObjectUrl = URL.createObjectURL(blob);
  imgSource.value = currentObjectUrl;
}

function downloadImage() {
  const inferred = fileName.value || "image";
  const fname = filenameFromHeaders(lastHeaders, inferred);
  if (imgSource.value) triggerDownload(imgSource.value, fname);
}

function cleanupObjectUrl() {
  if (currentObjectUrl) URL.revokeObjectURL(currentObjectUrl);
  currentObjectUrl = null;
}

// ── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(() => {
  loadImage().catch((e) => (errorMsg.value = e?.message || String(e)));
});

watch(
  () => [props.file, props.type, props.name],
  () => {
    loadImage().catch((e) => (errorMsg.value = e?.message || String(e)));
  },
);

onUnmounted(() => {
  cleanupObjectUrl();
  imgSource.value = null;
});
</script>

<style scoped>
img {
  display: block;
  /* Smooth downscaling for large bitmaps */
  image-rendering: auto;
}
</style>
